/**
 * <dbr-zoom-polaroid> — palco de tela cheia (position: sticky nativo, ver
 * dbr-vitrine.js pro motivo de não usar GSAP pin) onde um card pequeno
 * (proporção configurável) cresce conforme o scroll: a largura vai até
 * tela cheia (ou container), a altura para de crescer numa altura máxima
 * configurável. A MOLDURA fica sempre centralizada na tela (área visível
 * abaixo do header) — quem se move é a foto: object-position fixa o
 * ponto de foco num ponto da FOTO, e um translate estático
 * (xPercent/yPercent, calculado a partir desse mesmo ponto) cancela esse
 * deslocamento, deixando esse ponto sempre exatamente no centro do
 * card/tela. Como o transform-origin do GSAP é o centro por padrão, o
 * zoom (scale animado no scroll) automaticamente amplia em torno desse
 * ponto, sem precisar recalcular nada. Ao terminar de crescer, a moldura
 * (a "foto" dela é uma janela vazada que mostra a foto principal por
 * trás, já focada) desliza/aparece por cima.
 */
class DbrZoomPolaroid extends HTMLElement {
  connectedCallback() {
    this.card = this.querySelector('.dbr-zoom-polaroid__card');
    this.image = this.querySelector('.dbr-zoom-polaroid__image');
    this.frame = this.querySelector('.dbr-zoom-polaroid__frame');
    this.wrapper = this.querySelector('.dbr-zoom-polaroid__stage-wrapper');
    this.stage = this.querySelector('.dbr-zoom-polaroid__stage');

    this.initialWidthPercent = parseFloat(this.dataset.initialWidth) || 24;
    this.initialWidthPercentMobile = parseFloat(this.dataset.initialWidthMobile) || 65;
    this.ratioW = parseFloat(this.dataset.initialRatioW) || 4;
    this.ratioH = parseFloat(this.dataset.initialRatioH) || 5;
    this.imageZoomExtra = parseFloat(this.dataset.imageZoomExtra) || 1.2;
    this.isContainer = this.dataset.containerWidth === 'container';
    this.zoomPointX = parseFloat(this.dataset.zoomPointX);
    this.zoomPointY = parseFloat(this.dataset.zoomPointY);
    if (isNaN(this.zoomPointX)) this.zoomPointX = 50;
    if (isNaN(this.zoomPointY)) this.zoomPointY = 30;
    this.cardMaxHeightPercent = parseFloat(this.dataset.cardMaxHeight) || 100;
    this.framePhotoHeightPercent = parseFloat(this.dataset.framePhotoHeightPercent) || 100;
    this.framePhotoHeightPercentMobile = parseFloat(this.dataset.framePhotoHeightPercentMobile) || 100;

    if (!this.card || !this.image || !this.frame || !this.wrapper || !this.stage) return;

    // No estado inicial (card pequeno) queremos a foto 100% original, sem
    // cortar — como o card usa object-fit:cover, isso só acontece se a
    // proporção do card bater EXATAMENTE com a proporção real da foto.
    // "Proporção do card inicial" (initial_ratio) é só um palpite fixo
    // (4:5 etc) que quase nunca bate com a foto de verdade.
    //
    // Fonte principal: data-image-aspect-ratio, vindo direto dos
    // metadados do arquivo no Shopify (Liquid `image.aspect_ratio` =
    // largura/altura) — disponível na hora, sem depender do <img>
    // terminar de carregar no navegador. O evento 'load' do <img> é só
    // um respaldo (ex: se o metadado não vier por algum motivo) —
    // esperar só por ele se mostrou pouco confiável (lazy-load + timing
    // do iframe do editor de temas), causando corte visível até o load
    // disparar (ou nunca disparar a tempo).
    this.imageNaturalRatio = null;
    var self = this;
    var aspectRatioWH = parseFloat(this.dataset.imageAspectRatio);
    if (aspectRatioWH > 0) {
      this.imageNaturalRatio = 1 / aspectRatioWH;
    } else if (this.image.complete && this.image.naturalWidth) {
      this.imageNaturalRatio = this.image.naturalHeight / this.image.naturalWidth;
    } else {
      this.image.addEventListener('load', function onImageLoad() {
        self.image.removeEventListener('load', onImageLoad);
        self.imageNaturalRatio = self.image.naturalHeight / self.image.naturalWidth;
        // Recalcula o tamanho inicial do card com a proporção real agora
        // conhecida — invalidateOnRefresh (já ligado no timeline) refaz
        // os valores funcionais (initialW/initialH) e re-renderiza no
        // progresso atual do scroll.
        if (self.tl && self.tl.scrollTrigger) {
          self.tl.scrollTrigger.refresh();
        } else {
          self.setFinalState();
        }
      });
    }

    this.applyHeaderOffset();
    this.fitFramePhotoHeight();
    this._onResize = function () {
      self.applyHeaderOffset();
      self.fitFramePhotoHeight();
    };
    window.addEventListener('resize', this._onResize);

    if (window.DbrAnim && window.DbrAnim.reducedMotion) {
      this.setFinalState();
      return;
    }

    if (window.DbrAnim) {
      window.DbrAnim.ready(this.setup.bind(this));
    } else {
      this.setFinalState();
    }
  }

  /**
   * O header (dbr-header--sticky) fica em position:fixed/sticky por cima
   * do palco durante TODO o scroll pinado (não só no início) — sem
   * compensar isso, o conteúdo centraliza em relação aos 100vh inteiros
   * da tela e fica visualmente puxado pra cima, embaixo do header. Mede a
   * altura real do header (só se ele realmente estiver fixed/sticky) e
   * aplica como padding-top do palco via CSS var — assim o
   * align-items:center passa a centralizar só na área de fato visível.
   */
  measureHeaderOffset() {
    var header = document.querySelector('.dbr-header');
    if (!header) return 0;
    var position = getComputedStyle(header).position;
    if (position !== 'fixed' && position !== 'sticky') return 0;
    return header.getBoundingClientRect().height;
  }

  applyHeaderOffset() {
    this.headerOffset = this.measureHeaderOffset();
    this.style.setProperty('--dbr-zoom-header-offset', this.headerOffset + 'px');
  }

  /**
   * A largura da moldura é um controle horizontal puro (vw, editável) —
   * a ALTURA da foto dentro dela é calculada aqui pra preencher o espaço
   * vertical realmente disponível (abaixo do header, com uma margem de
   * respiro), ao invés de um vh fixo que ou sobra espaço (moldura pequena
   * demais no meio da tela) ou estoura (moldura grande demais off-screen).
   * "Altura da foto na moldura" no editor é uma % desse espaço
   * preenchível — 100% = preenche tudo, sem transbordar; valores menores
   * deixam a moldura mais compacta de propósito.
   */
  fitFramePhotoHeight() {
    var win = this.frame.querySelector('.dbr-zoom-polaroid__frame-window');
    if (!win) return;
    var isMobile = window.matchMedia('(max-width: 749px)').matches;
    var percent = isMobile ? this.framePhotoHeightPercentMobile : this.framePhotoHeightPercent;

    // Reseta pro fallback CSS primeiro, pra medir quanto espaço o padding
    // + legenda (tudo que NÃO é a janela) realmente ocupam no tamanho
    // atual — offsetHeight (não getBoundingClientRect) de propósito:
    // esse método roda de novo no resize, depois que o setup() já
    // aplicou scale via GSAP na moldura (0.88 durante o slide-in, por
    // exemplo), e getBoundingClientRect() refletiria esse scale visual,
    // distorcendo a medição do tamanho real (pré-transform).
    win.style.height = '';
    var frameH = this.frame.offsetHeight;
    var winH = win.offsetHeight;
    if (!frameH || !winH) return;
    var otherContentHeight = frameH - winH;

    var stageRect = this.stage.getBoundingClientRect();
    var availableHeight = stageRect.height - (this.headerOffset || 0);
    // Margem de respiro (4%) pra moldura não encostar nas bordas da área
    // visível mesmo em 100%.
    var maxFrameHeight = availableHeight * 0.96;
    var fillableWinHeight = Math.max(maxFrameHeight - otherContentHeight, 80);
    win.style.height = fillableWinHeight * (percent / 100) + 'px';
  }

  getPageWidthPx() {
    var value = getComputedStyle(document.documentElement).getPropertyValue('--page-width');
    var parsed = parseFloat(value);
    return parsed || 1200;
  }

  getDims() {
    var stageRect = this.stage.getBoundingClientRect();
    var finalW = this.isContainer ? Math.min(stageRect.width, this.getPageWidthPx()) : stageRect.width;
    // getBoundingClientRect().height do stage é sempre 100vh (border-box
    // não muda com padding) — subtrai o header pra "100% de altura"
    // significar 100% da área realmente visível abaixo dele, não da tela
    // inteira (senão a foto ultrapassaria o palco por trás do header).
    var visibleStageHeight = stageRect.height - (this.headerOffset || 0);
    var finalH = visibleStageHeight * (this.cardMaxHeightPercent / 100);
    // No mobile o card inicial usa uma % maior da tela — proporção de
    // card de mobile (ocupa boa parte da largura), não desktop reduzido
    // (que fica minúsculo numa tela de ~375-430px).
    var isMobile = window.matchMedia('(max-width: 749px)').matches;
    var widthPercent = isMobile ? this.initialWidthPercentMobile : this.initialWidthPercent;
    var initialW = finalW * (widthPercent / 100);
    // Usa a proporção REAL da foto (quando já conhecida) pro card
    // inicial — com object-fit:cover, box e foto na mesma proporção
    // significa zero corte. Cai pro "Proporção do card inicial"
    // configurado só como respaldo antes da foto carregar.
    var initialH = this.imageNaturalRatio ? initialW * this.imageNaturalRatio : (initialW * this.ratioH) / this.ratioW;
    return { initialW: initialW, initialH: initialH, finalW: finalW, finalH: finalH };
  }

  /**
   * A MOLDURA INTEIRA (foto + legenda) é o que fica centralizado na
   * tela — não só a janela da foto. O padding esquerdo/direito é sempre
   * igual (ver SCSS), então a janela já fica horizontalmente centrada
   * sozinha; esse cálculo cobre só o eixo X, como segurança caso o
   * padding lateral mude no futuro e deixe de ser simétrico. No eixo Y
   * não corrigimos nada de propósito: a legenda abaixo da foto pesa mais
   * que o respiro de cima, então "centralizar a janela" (como uma versão
   * anterior fazia) descentralizava visualmente a moldura como um todo —
   * o pedido foi centralizar a MOLDURA, então deixamos o yPercent:-50
   * padrão do GSAP fazer isso sozinho.
   */
  getFrameAnchor() {
    var win = this.frame.querySelector('.dbr-zoom-polaroid__frame-window');
    if (!win) return { x: 0 };
    var frameRect = this.frame.getBoundingClientRect();
    var winRect = win.getBoundingClientRect();
    var winCenterXRel = winRect.left - frameRect.left + winRect.width / 2;
    var frameCenterXRel = frameRect.width / 2;
    return {
      x: frameCenterXRel - winCenterXRel,
    };
  }

  setup(gsap, ScrollTrigger) {
    var self = this;

    gsap.set(this.card, {
      width: function () {
        return self.getDims().initialW;
      },
      height: function () {
        return self.getDims().initialH;
      },
    });

    // transform-origin fica FIXO no ponto de foco (não no centro) — assim
    // o scale nunca move esse ponto, qualquer que seja o valor do zoom.
    // IMPORTANTE: transform-origin é setado via CSS puro, nunca dentro de
    // um gsap.set() que também define xPercent/yPercent — passar os dois
    // juntos faz o GSAP calcular o translate errado (dobra o
    // deslocamento), confirmado empiricamente.
    this.image.style.transformOrigin = this.zoomPointX + '% ' + this.zoomPointY + '%';
    // xPercent/yPercent (o translate que leva o ponto de foco até o
    // centro da tela) começam em 0 aqui — NÃO no valor final. Esse
    // translate faz parte do MESMO tween do zoom logo abaixo, indo de 0
    // até (50 - zoomPoint)%: se aplicássemos o translate final de cara
    // (como uma versão anterior fazia), o enquadramento já nascia
    // deslocado pro ponto de foco, cortando a foto mesmo com scale:1 —
    // era exatamente o bug relatado ("já fica focado desde o início").
    // Com os dois animando juntos, no repouso (scroll 0) fica sem pan
    // nenhum — foto inteira, sem corte — e o foco só vai se aproximando
    // suavemente conforme rola.
    gsap.set(this.image, { xPercent: 0, yPercent: 0 });

    var anchor = this.getFrameAnchor();
    gsap.set(this.frame, {
      xPercent: -50,
      yPercent: -50,
      x: anchor.x,
      y: 48,
      autoAlpha: 0,
      scale: 0.88,
    });

    var tl = gsap.timeline({
      scrollTrigger: {
        trigger: this.wrapper,
        start: 'top top',
        end: 'bottom bottom',
        scrub: 1,
        invalidateOnRefresh: true,
      },
    });

    tl.fromTo(
      this.card,
      {
        width: function () {
          return self.getDims().initialW;
        },
      },
      {
        width: function () {
          return self.getDims().finalW;
        },
        ease: 'none',
        duration: 0.6,
      },
      0
    )
      // Altura para de crescer bem antes da largura — só a largura
      // continua até ficar cheia/container.
      .fromTo(
        this.card,
        {
          height: function () {
            return self.getDims().initialH;
          },
        },
        {
          height: function () {
            return self.getDims().finalH;
          },
          ease: 'none',
          duration: 0.35,
        },
        0
      )
      .fromTo(
        this.image,
        { scale: 1, xPercent: 0, yPercent: 0 },
        {
          scale: this.imageZoomExtra,
          xPercent: 50 - this.zoomPointX,
          yPercent: 50 - this.zoomPointY,
          ease: 'none',
          duration: 0.6,
        },
        0
      )
      .fromTo(
        this.frame,
        { autoAlpha: 0, scale: 0.88, y: 48 },
        { autoAlpha: 1, scale: 1, y: 0, ease: 'none', duration: 0.45 },
        0.55
      );

    this.tl = tl;
  }

  setFinalState() {
    var dims = this.getDims();
    this.card.style.width = dims.finalW + 'px';
    this.card.style.height = dims.finalH + 'px';
    var imgXPercent = 50 - this.zoomPointX;
    var imgYPercent = 50 - this.zoomPointY;
    this.image.style.transformOrigin = this.zoomPointX + '% ' + this.zoomPointY + '%';
    this.image.style.transform =
      'translate(' + imgXPercent + '%, ' + imgYPercent + '%) scale(' + this.imageZoomExtra + ')';
    var anchor = this.getFrameAnchor();
    this.frame.style.opacity = '1';
    this.frame.style.transform = 'translate(-50%, -50%) translate(' + anchor.x + 'px, 0)';
  }

  disconnectedCallback() {
    if (this.tl) {
      this.tl.scrollTrigger && this.tl.scrollTrigger.kill();
      this.tl.kill();
    }
    if (this._onResize) {
      window.removeEventListener('resize', this._onResize);
    }
  }
}

if (!customElements.get('dbr-zoom-polaroid')) {
  customElements.define('dbr-zoom-polaroid', DbrZoomPolaroid);
}
